import { memo, useEffect, useState } from "react";

import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import { CartItem } from "@zenith/features/Payments/interfaces";
import { CurrencyFieldComponent } from "@zenith/components";
import {
  PRODUCT_PLACEHOLDER_LABEL,
  UNIT_PRICE_LABEL,
} from "@zenith/data/constants";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";

/**
 * Props for Update Cart Component.
 * @interface
 * @name UpdateCartProps
 * @kind interface
 */
interface UpdateCartProps {
  /**
   * If `true`, the Update Cart is open.
   * @property
   * @name isUpdateCartOpen
   * @kind property
   * @memberof UpdateCartProps
   * @type {boolean}
   */
  isUpdateCartOpen: boolean;
  /**
   * Callback fired when the Update Modal is closed.
   * @method
   * @name closeUpdateModal
   * @kind property
   * @memberof UpdateCartProps
   * @returns {void}
   */
  closeUpdateModal: () => void;
  /**
   * Callback fired when the Modal is closed.
   * @method
   * @name closeModal
   * @kind property
   * @memberof UpdateCartProps
   * @returns {void}
   */
  closeModal: () => void;
  /**
   * Cart item.
   * @property
   * @name item
   * @kind property
   * @memberof UpdateCartProps
   * @type {CartItem | undefined}
   */
  item: CartItem | undefined;
  /**
   * Callback fired when the Cart Item is updated.
   * @method
   * @name handleUpdateCartItem
   * @kind property
   * @memberof UpdateCartProps
   * @param {CartItem} itemToUpdate Cart Item to be updated.
   * @param {string} amount Item amount.
   * @param {number | null} quantity Item quantity.
   * @param {string} description Item description.
   * @returns {void}
   */
  handleUpdateCartItem: (
    itemToUpdate: CartItem,
    amount: string,
    quantity: number | null,
    description: string
  ) => void;
}

/**
 * Memoized Update Cart Component.
 * @constant
 * @name UpdateCartComponent
 * @kind variable
 * @type {React.NamedExoticComponent<UpdateCartProps>}
 */
const UpdateCartComponent: React.NamedExoticComponent<UpdateCartProps> = memo(
  /**
   * Update Cart Component.
   * @function
   * @name UpdateCartComponent
   * @kind function
   * @memberof UpdateCartComponent
   * @param {UpdateCartProps} props Props for Update Cart Component.
   * @returns {JSX.Element} Update Cart Component.
   */
  function UpdateCartComponent(props: UpdateCartProps): JSX.Element {
    const {
      isUpdateCartOpen,
      closeUpdateModal,
      closeModal,
      item,
      handleUpdateCartItem,
    } = props;

    const [amount, setAmount] = useState<string>("");
    const [quantity, setQuantity] = useState<number | null>(null);
    const [description, setDescription] = useState<string>("");
    const [apuError, setApuError] = useState("");

    // Start of UpdateCart Logic.

    useEffect(() => {
      if (item) {
        setAmount(item.amount || "");
        setQuantity(item.quantity || null);
        setDescription(item.description || "");
      }
    }, [item]);

    const handleUpdate = () => {
      if (item) {
        handleUpdateCartItem(item, amount, quantity, description);
      }
      closeUpdateModal();
    };

    const handleCancel = () => {
      setAmount(item?.amount || "");
      setQuantity(item?.quantity || null);
      setDescription(item?.description || "");
      closeUpdateModal();
    };

    const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event) {
        let value = event.target.value;
        setApuError("");
        value = value
          .replace(/^0+(?=\d)/, "")
          .replace(/^0+(\d)(?=\.\d*)/, "$1");

        if (value.startsWith(".")) {
          value = `0${value}`;
        }

        setAmount(value);
      }
    };

    const handleQuantityChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const regex = /^[1-9][0-9]{0,4}$/;

      const cleanedInput = event.target.value.replace(/[^0-9]/g, "");
      let quantityValue: number | null = parseInt(cleanedInput);

      if (!regex.test(cleanedInput)) {
        quantityValue = null;
      }
      if (event.target.value.length > 5) {
        quantityValue = parseInt(event.target.value.slice(0, 5));
      }
      setQuantity(quantityValue);
    };

    const handleDescriptionChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const newDescription = event.target.value;
      const isValid = /^[A-Za-z0-9 ]*$/.test(newDescription);

      if (isValid && newDescription.length <= 50) {
        setDescription(newDescription);
      }
    };
    const handleAmountFocus = () => {
      if (amount.trim() === "") {
        setApuError("Amount required");
      } else if (parseFloat(amount) === 0) {
        setApuError("Amount must be greater than zero");
      } else {
        setApuError("");
      }
    };
    // End of UpdateCart Logic.

    return (
      <Modal open={isUpdateCartOpen} onClose={closeUpdateModal}>
        <div className="fixed top-0 left-0 flex h-full w-full items-center justify-center">
          <div className="h-132 w-120 rounded-2.5xl bg-white py-6 px-8">
            <div className="mb-3 flex items-start justify-between">
              <Button
                id="back-cart-summary"
                onClick={handleCancel}
                className="text-base font-normal normal-case text-neutral-600 hover:bg-white"
              >
                <ChevronLeft fontSize="small" className="mr-1.5" /> Back to Cart
              </Button>

              <IconButton
                id="close-update-button"
                onClick={() => {
                  closeUpdateModal();
                  closeModal();
                }}
                className="mb-8 text-neutral-900 hover:bg-white"
              >
                <CloseIcon />
              </IconButton>
            </div>
            <h1 className="mb-4 text-2xl font-medium text-neutral-900">
              Update Item
            </h1>
            <div>
              <form id="cart-form" className="flex w-full flex-col">
                <CurrencyFieldComponent
                  id="apu-input"
                  className="w-full"
                  label={UNIT_PRICE_LABEL}
                  value={amount}
                  errorMessage={apuError}
                  hasError={Boolean(apuError)}
                  onBlur={handleAmountFocus}
                  onChange={handleAmountChange}
                />

                <label className="flex flex-col text-sm font-normal leading-loose text-neutral-500">
                  Quantity
                  <input
                    id="quantity-button"
                    type="text"
                    min="1"
                    value={quantity === null ? "" : quantity}
                    placeholder="1"
                    className={`mb-2 rounded-lg border border-neutral-200 px-4 py-3 text-lg leading-normal text-black focus:outline-neutral-900`}
                    maxLength={5}
                    onChange={handleQuantityChange}
                  />
                </label>

                <label className="flex flex-col  text-sm font-normal leading-loose text-neutral-500">
                  Item Description
                  <input
                    type="text"
                    id="item-description-input"
                    value={description}
                    placeholder={PRODUCT_PLACEHOLDER_LABEL}
                    className={`mb-2 rounded-lg border border-neutral-200 px-4 py-3 text-lg leading-normal text-black focus:outline-neutral-900`}
                    maxLength={50}
                    onChange={handleDescriptionChange}
                  />
                </label>
              </form>

              <div className="mt-46">
                <Button
                  id="cancel-update-item-button"
                  onClick={handleCancel}
                  className="mr-4 h-12 w-73 rounded-lg border-1 border-solid border-neutral-200 py-3 px-10 text-base font-normal normal-case text-neutral-600 hover:border-neutral-200 hover:bg-white"
                  size="large"
                >
                  Cancel
                </Button>

                <Button
                  id="update-item-button"
                  type="button"
                  form="cart-form"
                  onClick={handleUpdate}
                  className={`h-12 w-73 rounded-lg py-3 px-10 text-base font-medium normal-case ${
                    amount === "" || Number(amount) === 0
                      ? "cursor-not-allowed bg-neutral-200 text-neutral-200"
                      : "bg-primary-300 text-white hover:bg-primary-300"
                  }`}
                  disabled={amount === "" || Number(amount) === 0}
                  size="large"
                >
                  Update Item
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  }
);

export default UpdateCartComponent;
